import React, { lazy } from 'react';
// Performance: Use CSS animations instead of framer-motion for above-fold content
// This avoids loading the framer-motion library for the LCP section
function smoothScrollToPricing(e: React.MouseEvent<HTMLAnchorElement>) {
  e.preventDefault();
  const duration = 1800; // 1.8s "deslizando" feel
  const startY = window.pageYOffset;
  let startTime: number | null = null;
  // easeInOutCubic for a smooth, controlled glide
  const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  // Land so:
  //  - TOP of viewport: "RECOMENDADO" ribbon (top of pricing-cards) is visible with breathing room
  //  - BOTTOM of viewport: green CTA button is visible (on tall screens both fit; on short screens we cap at CTA-bottom)
  const getTargetY = () => {
    const cards = document.getElementById('pricing-cards');
    const cta = document.getElementById('pricing-cta');
    // Preferred: land with the cards' top near the top of viewport
    const topOffset = 40;
    if (cards) {
      const cardsTopY = cards.getBoundingClientRect().top + window.pageYOffset - topOffset;
      // If we have the CTA, ensure it's not pushed off-screen below.
      // Compute the maximum scroll Y such that CTA bottom stays within viewport (with 24px bottom padding).
      if (cta) {
        const ctaRect = cta.getBoundingClientRect();
        const ctaBottomAbs = ctaRect.bottom + window.pageYOffset;
        const maxYForCtaVisible = ctaBottomAbs - window.innerHeight + 24;
        // Use the smaller of the two so CTA is always visible
        return Math.max(0, Math.min(cardsTopY, maxYForCtaVisible));
      }
      return cardsTopY;
    }
    const section = document.getElementById('pricing');
    if (section) {
      return section.getBoundingClientRect().top + window.pageYOffset - topOffset;
    }
    return startY;
  };
  const step = (timestamp: number) => {
    if (startTime === null) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easeInOutCubic(progress);
    // Recompute target each frame to account for lazy-loaded layout shifts
    const targetY = getTargetY();
    const distance = targetY - startY;
    window.scrollTo(0, startY + distance * eased);
    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      // Final correction in case content shifted after animation completed
      window.scrollTo({
        top: getTargetY(),
        behavior: 'auto'
      });
    }
  };
  requestAnimationFrame(step);
}
export function HeroSection() {
  return <section className="pt-4 pb-12 px-4 bg-slate-950 text-center" data-id="element-155">
      <div className="max-w-4xl mx-auto" data-id="element-156">
        <div className="flex justify-center mb-6" data-id="element-157">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-amber-500/40 bg-amber-500/5 text-amber-500 md:text-sm font-bold tracking-[0.2em] uppercase text-[8px]" data-id="element-158">
            <span aria-hidden="true" data-id="element-159">🚴</span>
            PROTORRENDA 4-12 · MÉTODO DE LOS 4 PILARES
          </span>
        </div>

        <h1 className="md:text-5xl lg:text-6xl font-serif font-bold text-white mb-10 tracking-tight text-[25px]" data-id="element-160">
          Tu entrenamiento de ciclismo{' '}
          <span className="text-amber-500 italic" data-id="element-161">listo. Desde hoy.</span>
        </h1>

        <div className="relative w-full max-w-md mx-auto mb-10" data-id="element-162">
          <img src="https://cdn.magicpatterns.com/uploads/7EYMj6MZzTYg5opvzc8sU6/Diseno_sin_titulo_(6).webp" alt="Protorrenda 4-12 — Método de entrenamiento para ciclistas amateurs argentinos." className="w-full h-auto rounded-2xl shadow-2xl" width={665} height={665} sizes="(max-width: 768px) 90vw, 448px" loading="eager" fetchPriority="high" decoding="sync" data-id="element-163" />
        </div>

        <div data-id="element-164">
          <p className="md:text-2xl text-slate-400 font-medium max-w-2xl mx-auto mb-6 text-[16px]" data-id="element-165">
            Calendario, nutrición, biomecánica y estrategia de subidas listos.{' '}
            <span className="text-amber-500 font-bold italic" data-id="element-166">
              Sin improvisar ni perder sábados.
            </span>
          </p>

          <a href="#pricing" onClick={smoothScrollToPricing} className="inline-flex items-center justify-center gap-3 py-4 px-8 rounded-2xl bg-green-600 hover:bg-green-500 text-white font-black text-lg md:text-xl transition-colors shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_30px_rgba(34,197,94,0.4)] mb-10" data-id="element-167">
            <span className="text-2xl" data-id="element-168">🚴</span> → QUIERO MI SISTEMA PROTORRENDA
            AHORA
          </a>

          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-3 text-sm text-slate-400 max-w-3xl mx-auto" data-id="element-169">
            <span className="flex items-center gap-1" data-id="element-170">📄 100% PDF</span>
            <span className="hidden md:inline text-slate-700" data-id="element-171">•</span>
            <span className="flex items-center gap-1" data-id="element-172">⚡ Acceso inmediato</span>
            <span className="hidden md:inline text-slate-700" data-id="element-173">•</span>
            <span className="flex items-center gap-1" data-id="element-174">
              🇦🇷 Para el ciclista amateur argentino
            </span>
            <span className="hidden md:inline text-slate-700" data-id="element-175">•</span>
            <span className="flex items-center gap-1" data-id="element-176">
              🚵 Todas las tipologías de salida
            </span>
            <span className="hidden md:inline text-slate-700" data-id="element-177">•</span>
            <span className="flex items-center gap-1" data-id="element-178">🛡️ Garantía 7 días</span>
            <span className="hidden md:inline text-slate-700" data-id="element-179">•</span>
            <span className="flex items-center gap-1" data-id="element-180">🔒 Pago seguro</span>
            <span className="hidden md:inline text-slate-700" data-id="element-181">•</span>
            <span className="flex items-center gap-1" data-id="element-182">💳 Mercado Pago</span>
            <span className="hidden md:inline text-slate-700" data-id="element-183">•</span>
            <span className="flex items-center gap-1" data-id="element-184">
              🏅 +200 ciclistas ya lo aplican
            </span>
          </div>
        </div>
      </div>
    </section>;
}